import React, { useState, useEffect, useCallback } from "react";
import GoalForm from "./GoalForm";
import GoalList from "./GoalList";
import UserProfile from "./UserProfile";
import { buildApiUrl, getAuthHeaders } from "../config/api";
import "./dashboard/dashboard.css";

const Dashboard = ({ user, onLogout }) => {
    const [goals, setGoals] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState("goals");
    const [filters, setFilters] = useState({
        category: "",
        priority: "",
        completed: ""
    });

    const fetchGoals = useCallback(async () => {
        setLoading(true);
        try {
            const queryParams = new URLSearchParams();
            Object.entries(filters).forEach(([key, value]) => {
                if (value) queryParams.append(key, value);
            });

            const response = await fetch(
                buildApiUrl(`/api/goals?${queryParams}`),
                { headers: getAuthHeaders() }
            );

            if (response.ok) {
                const data = await response.json();
                setGoals(data);
            } else if (response.status === 401) {
                onLogout();
            }
        } catch (error) {
            console.error("Error fetching goals:", error);
        } finally {
            setLoading(false);
        }
    }, [filters, onLogout]);

    const fetchStats = useCallback(async () => {
        try {
            const response = await fetch(
                buildApiUrl("/api/goals/stats"),
                { headers: getAuthHeaders() }
            );

            if (response.ok) {
                const data = await response.json();
                setStats(data);
            }
        } catch (error) {
            console.error("Error fetching stats:", error);
        }
    }, []);

    useEffect(() => {
        document.body.classList.add("workspace-active");
        fetchStats();
        return () => {
            document.body.classList.remove("workspace-active");
        };
    }, [fetchStats]);

    useEffect(() => {
        fetchGoals();
    }, [fetchGoals]);

    const handleGoalAdded = (newGoal) => {
        setGoals((prevGoals) => [newGoal, ...prevGoals]);
        fetchStats();
    };

    const handleGoalDeleted = (goalId) => {
        setGoals((prevGoals) => prevGoals.filter((goal) => goal.id !== goalId));
        fetchStats();
    };

    const handleGoalUpdated = (updatedGoal) => {
        setGoals((prevGoals) => prevGoals.map((goal) => (goal.id === updatedGoal.id ? updatedGoal : goal)));
        fetchStats();
    };

    const handleFilterChange = (newFilters) => {
        setFilters(newFilters);
    };

    const scrollToComposer = () => {
        const element = document.getElementById("goal-composer");
        if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    };

    return (
        <div className="workspace">
            <header className="workspace__topbar">
                <div className="workspace__brand">
                    <span className="workspace__logo">DoneLog</span>
                    <span className="workspace__tagline">完成每一件想做的事</span>
                </div>
                <div className="workspace__session">
                    <span className="workspace__session-email">{user.email}</span>
                    <button className="btn secondary" onClick={onLogout}>
                        退出登录
                    </button>
                </div>
            </header>

            <section className="workspace__hero">
                <div className="workspace__intro">
                    <span className="workspace__eyebrow">欢迎回来</span>
                    <h1>Hi，{user.username}！今天想完成什么？</h1>
                    <p>
                        DoneLog 会记录你的每一个阶段成果，帮助你保持节奏、发现风险并沉淀经验。
                    </p>
                    {stats ? (
                        <div className="workspace__stats">
                            <div className="workspace-stat">
                                <span className="workspace-stat__label">累计完成</span>
                                <span className="workspace-stat__value">{stats.completed_goals}</span>
                            </div>
                            <div className="workspace-stat">
                                <span className="workspace-stat__label">开放中的目标</span>
                                <span className="workspace-stat__value">{stats.total_goals - stats.completed_goals}</span>
                            </div>
                            <div className="workspace-stat">
                                <span className="workspace-stat__label">完成率</span>
                                <span className="workspace-stat__value">{stats.completion_rate}%</span>
                            </div>
                        </div>
                    ) : (
                        <div className="workspace__stats">
                            <div className="workspace-stat">
                                <span className="workspace-stat__label">累计完成</span>
                                <span className="workspace-stat__value">--</span>
                            </div>
                            <div className="workspace-stat">
                                <span className="workspace-stat__label">开放中的目标</span>
                                <span className="workspace-stat__value">--</span>
                            </div>
                            <div className="workspace-stat">
                                <span className="workspace-stat__label">完成率</span>
                                <span className="workspace-stat__value">--</span>
                            </div>
                        </div>
                    )}
                    <div className="workspace__actions">
                        <button type="button" className="btn primary" onClick={scrollToComposer}>
                            记录新的成果
                        </button>
                        <a className="btn secondary" href="#goal-feed">
                            浏览成就时间线
                        </a>
                    </div>
                </div>

                <aside className="workspace__side-summary">
                    <h3>今日建议</h3>
                    <ul>
                        <li>回顾上一条目标的完成笔记，补充复盘要点。</li>
                        <li>为下一个阶段设定明确的截止时间与责任人。</li>
                        <li>保持目标标签一致，便于后续统计与检索。</li>
                    </ul>
                    <button type="button" className="btn ghost" onClick={() => setActiveTab("profile")}>查看个人面板 →</button>
                </aside>
            </section>

            <div className="workspace__controls">
                <div className="workspace__tabs">
                    <button
                        type="button"
                        className={`workspace__tab ${activeTab === "goals" ? "workspace__tab--active" : ""}`}
                        onClick={() => setActiveTab("goals")}
                    >
                        成就空间
                    </button>
                    <button
                        type="button"
                        className={`workspace__tab ${activeTab === "profile" ? "workspace__tab--active" : ""}`}
                        onClick={() => setActiveTab("profile")}
                    >
                        个人中心
                    </button>
                </div>
            </div>

            <div className="workspace__stack">
                {activeTab === "goals" ? (
                    <>
                        <GoalForm onGoalAdded={handleGoalAdded} />
                        <GoalList
                            goals={goals}
                            loading={loading}
                            filters={filters}
                            onFilterChange={handleFilterChange}
                            onGoalDeleted={handleGoalDeleted}
                            onGoalUpdated={handleGoalUpdated}
                        />
                    </>
                ) : (
                    <UserProfile user={user} />
                )}
            </div>
        </div>
    );
};

export default Dashboard;
